import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  axApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const axSharedApi = {
  getNotes: async (id: string, type: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `notes`,
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("entity_type", type);
        query.push("entity_id", id);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  createNote: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters("ax", "notes");
    const response = await axApiClient.post(url, payload);
    return response.data;
  },

  getActivityLog: async (id: string, type: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `activity-log`,
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("patient_id", id);
        query.push("entity_type", type);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  getPriorityOptions: async () => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "ax",
      `choices`,
      ["priority"]
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },

  getAssetStatusList: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "choices/asset_status"
    );
    const response = await axApiClient.get(url);
    return response.data;
  },

  getIncidentStatusList: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "choices/incident_status"
    );
    const response = await axApiClient.get(url);
    return response.data;
  },

  getFacilitiesList: async (): Promise<any> => {
    const REMOTE_UTILITY_ID = getRemoteUtilityId();

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "facilities-info",
      (qs: QueryStringParameters) => {
        qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  getAllSystems: async (facilityId?: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "system-info",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        if (facilityId) {
          query.push("facility", facilityId);
        }
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  getNetworksList: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "network-info",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
  calculateCompliance: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "compliance/calculate-monthly"
    );
    const response = await axApiClient.post(url, payload);
    return response.data;
  },
  assessCalculation: async (payload: any): Promise<any> => {
    try {
      const response = await axApiClient.post("assessment/", payload);
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },
  getConditionHistory: async (
    id: number,
    entityType: number,
    page?: number
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `assessment/history/${id}`,
      (qs) => {
        qs.push("entity_type", entityType);
        if (page) {
          qs.push("page", page);
        }
        qs.push("limit", 10);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },

  getAssetType: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `config-map/asset_class_json`,
      (qs) => {
        qs.push("config_level", "asset_type");
        qs.push("remote_utility_id", getRemoteUtilityId());
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getNetworksTypeOptions: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/network_type",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
        qs.push("config_level", "network_type");
      }
    );
    const response = await axApiClient.get(url);
    return response.data.result;
  },

  getSystemTypeOptions: async (remoteUtilityId: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/system_json",
      (qs) => {
        qs.push("remote_utility_id", remoteUtilityId);
        qs.push("config_level", "system_type");
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },
  getFacilityTypeOptions: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/facility_type",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
        qs.push("config_level", "facility_type");
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },

  getNetworkChoices: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "choices/network_type"
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
  getAllSchedules: async (
    entityId: string,
    entityType: string,
    remoteUtilityId: number,
    page: number,
    limit: number,
    search?: string,
    status?: number,
    frequencyType?: string,
    criticality?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "schedules",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", page);
        query.push("limit", limit);
        if (search) query.push("search", search);
        if (status) query.push("status", status);
        if (frequencyType) query.push("frequency_type", frequencyType);
        if (criticality) query.push("criticality", criticality);
        query.push("entity_id", entityId);
        query.push("entity_type", entityType);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  getJobcardStatusChoices: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "choices/job_card_status"
    );
    const response = await axApiClient.get(url);
    return response.data.result;
  },

  getUnitOptions: async () => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "ax",
      `choices`,
      ["unit"]
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },

  getFilterEntities: async (
    entityId: string,
    entityType: string,
    filterType: string,
    page: number,
    limit: number
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `alert-asset-redirection/${entityId}`,
      (qs: QueryStringParameters) => {
        qs.push("context", entityType);
        qs.push("type", filterType);
        qs.push("page", page);
        qs.push("limit", limit);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },
};

export const deleteDocument = async (id: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    `documents/${id}/delete`,
    (qs) => {
      qs.push("remote_utility_id", getRemoteUtilityId());
    }
  );
  const response = await axApiClient.delete(url);
  return response.data;
};

export const getAssetCondition = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/condition",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

export const getConditionByType = async (
  className: string,
  typeCode: string
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("code_list", className);
      qs.push("code_name", typeCode);
      qs.push("config_level", "asset_type");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

export const getFacilitiesList = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "facilities-info",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    }
  );

  const response = await axApiClient.get(url);
  return response.data;
};

export const getElectricUnit = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/electric_unit"
  );

  const response = await axApiClient.get(url);
  return response.data.result;
};
